import React, { useMemo } from "react";
import { Screen } from '~common/screen';
import { Styled } from './styled'
import { StyledScreen, IProps as IScreenProps } from '~common/screen'
import MinusIcon from '~images/minus.svg'
import PlusIcon from '~images/plus.svg'
import { dividedByThree } from '~helpers/constant'
import { phoneNums } from '~helpers/constant'
import { useIsMobile } from '~helpers/hooks/useIsMobile'
import { fetchPrices } from '~/store'

interface IProps {
  screenProps?: IScreenProps,
  style?: any
}

const MAX_COUNT_USERS = 4900
const MIN_COUNT_USERS = 1
const DAYS_IN_MONTH = 30

const getLocaleUsers = (units: number) => {
  switch (new Intl.PluralRules('ru-RU').select(units)) {
    case 'one':
      return 'пользователь'
    case 'few':
      return 'пользователя'
    case 'many':
      return 'пользователей'
  }
}

const PriceCalculateScreen: React.FC<IProps> = (props) => {
  const { data: response, isLoading, isError } = fetchPrices()
  
  const isMobile = useIsMobile();
  const [isFocus, setFocus] = React.useState<boolean>(false)
  const [countUser, setCount] = React.useState<number>(MIN_COUNT_USERS)
  const input = React.useRef(null)
  const numUsersForPersonalConditions = 20
  const reg = /^\d*$/
  const units = getLocaleUsers(countUser)

  const PRICE_PER_USER = useMemo(() => {
    if (isLoading || isError) return 0

    const userService = response?.body?.data?.fee?.services?.find((service: any) => service.type === 'USER') || {}
    const dayCostUZS = userService?.cost_in_currency_per_day?.find((costPerDay: any) => costPerDay.currency === 'UZS')?.cost || 0
    // цена за день в атомарных единицах переводится в стоимость подрядком выше и умножается на расчетное количество дней
    const costFormatted = dayCostUZS / 100 * DAYS_IN_MONTH

    return costFormatted
  }, [isLoading, isError, response])

  const getDefaultTextUsers = () => {
    const counts = [3, 5, 10]
    return counts.map((count) => ({
      countUser: count,
      units: getLocaleUsers(count),
      calculate: PRICE_PER_USER * count
    }))
  }
  const defaultTextUsers = getDefaultTextUsers()

  const handleChange = (count: number) => setCount(count)
  const handleFocus = () => setFocus(!isFocus)

  const handleMinusCount = () => {
    if (countUser === 1) return
    const result = countUser - 1
    setCount(result)
  }
  const handlePlusCount = () => {
    if (countUser > MAX_COUNT_USERS) return
    const result = countUser + 1
    setCount(result)
  }

  const calculatePrice = () => {
    if (countUser === 0) return PRICE_PER_USER
    if (countUser > MAX_COUNT_USERS) return PRICE_PER_USER * MAX_COUNT_USERS
    return PRICE_PER_USER * countUser
  }

  const handlePress = (evt: any) => {
    if (evt.keyCode === 13) {
      if (countUser === 0) {
        setCount(MIN_COUNT_USERS)
      }
      if (countUser > MAX_COUNT_USERS) {
        setCount(MAX_COUNT_USERS)
      }
      calculatePrice()
      evt.target.blur()
    }
  }
  const calculate = calculatePrice()

  if (isError) {
    return (
      <Screen
      title={
        isMobile
          ? <span>Абонентская плата за <StyledScreen.GreenText><br />30 дней</StyledScreen.GreenText></span>
          : <span>Абонентская плата за <StyledScreen.GreenText>30 дней</StyledScreen.GreenText></span>
      }
      bottomLine={true}
      style={props.style || {}}
      {...props.screenProps}
      >
        <Styled.Content isLoading=''>
          <Styled.Default>
            <Styled.DefaultResult>Не удалось загрузить цены</Styled.DefaultResult>
          </Styled.Default>
        </Styled.Content>
      </Screen>
    )
  }

  return (
    <Screen
      title={
        isMobile
          ? <span>Абонентская плата за <StyledScreen.GreenText><br />30 дней</StyledScreen.GreenText></span>
          : <span>Абонентская плата за <StyledScreen.GreenText>30 дней</StyledScreen.GreenText></span>
      }
      bottomLine={true}
      style={props.style || {}}
      {...props.screenProps}
    >
      <Styled.Content isLoading={isLoading ? 'yes' : ''}>
        <Styled.Wrapper>
          <Styled.Calculate>
            <Styled.CalculateBlock>
              <Styled.IncDecBtn onClick={handleMinusCount}>
                <MinusIcon />
              </Styled.IncDecBtn>
              <Styled.UsersCountInput
                ref={input}
                onKeyUp={handlePress}
                placeholder='Введите число'
                value={(Number.isInteger(countUser) && !isFocus && countUser + ` ${units}`) || countUser || ''}
                onChange={(evt) => {
                  if (+evt.target.value > MAX_COUNT_USERS) return handleChange(MAX_COUNT_USERS)
                  evt.target.value.match(reg) && handleChange(+evt.target.value)
                }}
                onFocus={handleFocus}
                onBlur={() => {
                  if (countUser === 0) {
                    setCount(MIN_COUNT_USERS)
                  }
                  if (countUser > MAX_COUNT_USERS) {
                    setCount(MAX_COUNT_USERS)
                  }
                  calculatePrice()
                  handleFocus()
                }}
              />
              <Styled.IncDecBtn onClick={handlePlusCount}>
                <PlusIcon />
              </Styled.IncDecBtn>
            </Styled.CalculateBlock>
            <Styled.Result>
              {
                countUser < numUsersForPersonalConditions
                  ? <Styled.ResultText>{`${dividedByThree(calculate, ' ')} сум`}</Styled.ResultText>
                  : <Styled.ResultTextPersonal>
                    Для получения индивидуальных условий, свяжитесь с отделом продаж
                    &nbsp;<Styled.ResultLink href={`tel:${phoneNums.salesDepartment.value}`}>{phoneNums.salesDepartment.formatted}</Styled.ResultLink>
                  </Styled.ResultTextPersonal>
              }
            </Styled.Result>
          </Styled.Calculate>
        </Styled.Wrapper>
        <Styled.Default>
          {defaultTextUsers.map((block) => (
            <Styled.DefaultBlock>
              <Styled.DefaultHeader>{`${block.countUser} ${block.units}`}</Styled.DefaultHeader>
              <Styled.DefaultResult>{`${block.calculate.toLocaleString()} сум`}</Styled.DefaultResult>
            </Styled.DefaultBlock>
          ))}
          <Styled.DefaultBlock key='20+_users'>
            <Styled.DefaultHeader>{`${numUsersForPersonalConditions}+ ${getLocaleUsers(numUsersForPersonalConditions)}`}</Styled.DefaultHeader>
            <Styled.DefaultResult>Персональная цена*</Styled.DefaultResult>
            <Styled.DefaultSubtitle>* только для новых клиентов</Styled.DefaultSubtitle>
          </Styled.DefaultBlock>
        </Styled.Default>
      </Styled.Content>
    </Screen>
  )
}

export { PriceCalculateScreen }
